import React, { useCallback, useEffect, useRef, useState } from "react";
import layout from "./AskKnowtexSidecar.module.css";

export const SMART_ASSIST_DEFAULT_WIDTH_PERCENT = 33;
export const SMART_ASSIST_MIN_WIDTH_PERCENT = 25;
export const SMART_ASSIST_MAX_WIDTH_PERCENT = 45;
export const SMART_ASSIST_SPLIT_MIN_WIDTH = 1280;
// Preserve native browser text scaling through 200%. Above that point, keep
// the text at 200% while expanding the layout surface so controls can reflow.
export const SMART_ASSIST_MAX_ZOOM = 2;

const initialDevicePixelRatio = typeof window === "undefined" ? 1 : window.devicePixelRatio || 1;
const initialViewportWidth = typeof window === "undefined" ? 0 : window.innerWidth;

export const getSmartAssistZoomLevel = () => {
  if (typeof window === "undefined" || !window.innerWidth) return 1;

  const dprZoom = (window.devicePixelRatio || 1) / initialDevicePixelRatio;
  const outerZoom = window.outerWidth ? window.outerWidth / window.innerWidth : 1;
  const isNearFullScreen =
    window.screen?.availWidth && window.outerWidth
      ? window.outerWidth >= window.screen.availWidth * 0.8
      : false;
  const screenZoom =
    isNearFullScreen && window.screen?.width ? window.screen.width / window.innerWidth : 1;
  const viewportZoom = window.visualViewport?.scale || 1;

  return Math.max(1, Math.round(Math.max(dprZoom, outerZoom, screenZoom, viewportZoom) * 20) / 20);
};

export const getSmartAssistLayoutMetrics = (visibleWidth, zoomLevel, baselineWidth = 0) => ({
  contentScale: zoomLevel > SMART_ASSIST_MAX_ZOOM ? SMART_ASSIST_MAX_ZOOM / zoomLevel : 1,
  isSplitView: Math.max(visibleWidth * zoomLevel, baselineWidth) >= SMART_ASSIST_SPLIT_MIN_WIDTH,
});

export const useSmartAssistLayout = (containerWidth) => {
  const [zoomLevel, setZoomLevel] = useState(getSmartAssistZoomLevel);

  useEffect(() => {
    const measureZoom = () => setZoomLevel(getSmartAssistZoomLevel());
    window.addEventListener("resize", measureZoom);
    window.visualViewport?.addEventListener("resize", measureZoom);
    return () => {
      window.removeEventListener("resize", measureZoom);
      window.visualViewport?.removeEventListener("resize", measureZoom);
    };
  }, []);

  const visibleWidth =
    Number(containerWidth) || (typeof window === "undefined" ? 0 : window.innerWidth);

  return {
    zoomLevel,
    ...getSmartAssistLayoutMetrics(visibleWidth, zoomLevel, initialViewportWidth),
  };
};

const clampWidthPercent = (widthPercent) =>
  Math.min(
    SMART_ASSIST_MAX_WIDTH_PERCENT,
    Math.max(
      SMART_ASSIST_MIN_WIDTH_PERCENT,
      Number(widthPercent) || SMART_ASSIST_DEFAULT_WIDTH_PERCENT
    )
  );

export default function AskKnowtexSidecar({
  children,
  isExpanded,
  onToggle,
  sidecarId,
  className = "",
  widthPercent = SMART_ASSIST_DEFAULT_WIDTH_PERCENT,
  onWidthChange,
  isFixedPanel = false,
  contentScale = 1,
}) {
  const resizeStateRef = useRef(null);
  const showButtonRef = useRef(null);
  const panelRef = useRef(null);
  const wasExpandedRef = useRef(isExpanded);
  const boundedWidthPercent = clampWidthPercent(widthPercent);
  const boundedContentScale = Math.min(1, Math.max(0.1, contentScale));
  const zoomSurfaceSize = `${100 / boundedContentScale}%`;

  const updateWidthFromClientX = useCallback(
    (clientX) => {
      if (typeof window === "undefined" || !window.innerWidth || !onWidthChange) return;
      onWidthChange(clampWidthPercent((clientX / window.innerWidth) * 100));
    },
    [onWidthChange]
  );

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (!resizeStateRef.current) return;
      updateWidthFromClientX(event.clientX);
    };
    const stopResizing = () => {
      if (!resizeStateRef.current) return;
      resizeStateRef.current = null;
      document.body.classList.remove(layout.resizing);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", stopResizing);
    window.addEventListener("pointercancel", stopResizing);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", stopResizing);
      window.removeEventListener("pointercancel", stopResizing);
      document.body.classList.remove(layout.resizing);
    };
  }, [updateWidthFromClientX]);

  // Toggling unmounts the Show button or hides the panel, so hand focus to
  // whichever one is now visible instead of dropping it on the page body.
  useEffect(() => {
    if (wasExpandedRef.current === isExpanded) return;
    wasExpandedRef.current = isExpanded;
    const active = document.activeElement;
    const focusIsLost =
      !active || active === document.body || Boolean(panelRef.current?.contains(active));
    if (!focusIsLost) return;
    if (isExpanded) panelRef.current?.focus();
    else showButtonRef.current?.focus();
  }, [isExpanded]);

  const handleResizeStart = (event) => {
    if (!onWidthChange) return;
    event.preventDefault();
    resizeStateRef.current = true;
    document.body.classList.add(layout.resizing);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleResizeKeyDown = (event) => {
    if (!onWidthChange) return;

    let nextWidth = boundedWidthPercent;
    if (event.key === "ArrowLeft") nextWidth -= event.shiftKey ? 5 : 1;
    else if (event.key === "ArrowRight") nextWidth += event.shiftKey ? 5 : 1;
    else if (event.key === "Home") nextWidth = SMART_ASSIST_MIN_WIDTH_PERCENT;
    else if (event.key === "End") nextWidth = SMART_ASSIST_MAX_WIDTH_PERCENT;
    else return;

    event.preventDefault();
    onWidthChange(clampWidthPercent(nextWidth));
  };

  return (
    <aside
      data-testid="ask-knowtex-sidecar"
      data-expanded={isExpanded}
      data-layout={isFixedPanel ? "fixed" : "overlay"}
      data-content-scale={boundedContentScale}
      className={`${layout.sidecar} ${isExpanded ? layout.expanded : layout.collapsed} ${
        isFixedPanel ? layout.fixedPanel : ""
      } ${className}`}
      style={{
        "--smart-assist-panel-width": `${boundedWidthPercent}vw`,
        "--smart-assist-content-scale": boundedContentScale,
      }}
    >
      <div
        className={layout.zoomSurface}
        data-testid="smart-assist-zoom-surface"
        data-zoom-capped={boundedContentScale < 1}
        style={{
          width: zoomSurfaceSize,
          height: zoomSurfaceSize,
          transform: `scale(${boundedContentScale})`,
        }}
      >
        {!isExpanded && (
          <button
            ref={showButtonRef}
            type="button"
            onClick={onToggle}
            aria-label="Show Smart Assist"
            aria-controls={sidecarId}
            aria-expanded="false"
            title="Show Smart Assist"
            className={layout.toggleButton}
          >
            <span className={layout.railLabel} aria-hidden="true">
              Smart Assist
            </span>
            <span className={layout.railChevron} aria-hidden="true">
              ›
            </span>
          </button>
        )}

        <div
          ref={panelRef}
          id={sidecarId}
          tabIndex={-1}
          role="region"
          aria-label="Smart Assist panel"
          hidden={!isExpanded}
          // React 18 has no `inert` prop; the empty string sets the attribute.
          inert={isExpanded ? undefined : ""}
          className={`${layout.panelContent} h-full min-w-0 overflow-y-auto focus:outline-none`}
        >
          {/* Stays mounted while collapsed so an in-flight response keeps streaming. */}
          {children}
        </div>

        {isExpanded && onWidthChange && (
          <div
            role="separator"
            aria-label="Resize Smart Assist panel"
            aria-orientation="vertical"
            aria-valuemin={SMART_ASSIST_MIN_WIDTH_PERCENT}
            aria-valuemax={SMART_ASSIST_MAX_WIDTH_PERCENT}
            aria-valuenow={Math.round(boundedWidthPercent)}
            aria-valuetext={`${Math.round(boundedWidthPercent)} percent of the window`}
            tabIndex="0"
            className={layout.resizeHandle}
            onPointerDown={handleResizeStart}
            onDoubleClick={() => onWidthChange(SMART_ASSIST_DEFAULT_WIDTH_PERCENT)}
            onKeyDown={handleResizeKeyDown}
            title="Drag to resize. Double-click to reset to 33%."
          >
            <span className={layout.resizeGrip} aria-hidden="true" />
          </div>
        )}
      </div>
    </aside>
  );
}
